import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';
import InstallPrompt from './components/InstallPrompt';
import { isNative, setDarkStatusBar, hideSplash } from './utils/native';
import './App.css';

// Pages
import HomePage from './pages/HomePage.jsx';
import SignInPage from './pages/SignInPage.jsx';
import SignUpPage from './pages/SignUpPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import AuthCallback from './pages/AuthCallback.jsx';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
import Terms from './pages/Terms.jsx';
import FeaturesPage from './pages/FeaturesPage.jsx';
import LeaderboardPage from './pages/LeaderboardPage.jsx';
import BlogPage from './pages/BlogPage.jsx';
import BlogArticle from './pages/BlogArticle.jsx';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Skip auth check if user was passed from AuthCallback
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/signin" />;
};

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id during render (not in useEffect)
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (isNative) {
      setDarkStatusBar();
      hideSplash();
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster />
          {!isNative && <InstallPrompt />}
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
